import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, Instagram, Zap, Plus } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import heic2any from "heic2any";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

const PRESETS = {
  "post": { name: "Instagram Post", width: 1080, height: 1080, description: "Square format for Instagram feed" },
  "story": { name: "Instagram Story", width: 1080, height: 1920, description: "Vertical format for Stories & Reels" },
  "landscape": { name: "Instagram Landscape", width: 1080, height: 566, description: "Landscape format for feed" },
  "portrait": { name: "Instagram Portrait", width: 1080, height: 1350, description: "Portrait format (4:5 ratio)" },
  "profile": { name: "Profile Picture", width: 320, height: 320, description: "Profile photo (displays at 110x110)" },
};

function ResizeForInstagram({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resizeProgress, setResizeProgress] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState("post");
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const preset = PRESETS[selectedPreset];

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Resize Image for Instagram - KayTool";
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Resize images for Instagram posts, stories, reels and profile. Free online Instagram image resizer with perfect dimensions.');
  }, []);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/resize-for-instagram`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
        }
      } catch (error) {
        console.log("No CMS content found");
      }
    };
    fetchPageContent();
  }, [API_URL]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    await processFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const processFiles = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic') || f.name.toLowerCase().endsWith('.heif'));
    
    if (imageFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }

    setIsProcessing(true);
    setResizeProgress(0);

    const newImages = [];

    for (let i = 0; i < imageFiles.length; i++) {
      let file = imageFiles[i];
      
      if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.95 });
          file = new File([convertedBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
        } catch (error) {
          toast.error(`Failed to convert ${file.name}`);
          continue;
        }
      }

      const dimensions = await getImageDimensions(file);

      newImages.push({
        id: Date.now() + i,
        name: file.name,
        file: file,
        preview: URL.createObjectURL(file),
        originalWidth: dimensions.width,
        originalHeight: dimensions.height,
        originalSize: file.size,
        resized: null,
        resizedWidth: null,
        resizedHeight: null,
        resizedSize: null,
      });

      setResizeProgress(Math.round(((i + 1) / imageFiles.length) * 100));
    }

    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    toast.success(`Added ${newImages.length} image${newImages.length > 1 ? 's' : ''}`);
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const resizeAndCropImage = async (file, targetWidth, targetHeight) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        const sourceRatio = img.width / img.height;
        const targetRatio = targetWidth / targetHeight;

        let sourceX = 0, sourceY = 0, sourceW = img.width, sourceH = img.height;

        if (sourceRatio > targetRatio) {
          sourceW = img.height * targetRatio;
          sourceX = (img.width - sourceW) / 2;
        } else {
          sourceH = img.width / targetRatio;
          sourceY = (img.height - sourceH) / 2;
        }

        ctx.drawImage(img, sourceX, sourceY, sourceW, sourceH, 0, 0, targetWidth, targetHeight);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.92);
        
        URL.revokeObjectURL(img.src);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleResize = async () => {
    if (images.length === 0) {
      toast.error("Please add images first");
      return;
    }

    setIsProcessing(true);
    setResizeProgress(0);

    const updatedImages = [];

    for (let i = 0; i < images.length; i++) {
      const image = images[i];

      try {
        const resizedBlob = await resizeAndCropImage(image.file, preset.width, preset.height);
        
        updatedImages.push({
          ...image,
          resized: resizedBlob,
          resizedWidth: preset.width,
          resizedHeight: preset.height,
          resizedSize: resizedBlob.size,
          resizedPreview: URL.createObjectURL(resizedBlob),
          presetUsed: selectedPreset,
        });
      } catch (error) {
        toast.error(`Failed to resize ${image.name}`);
        updatedImages.push(image);
      }

      setResizeProgress(Math.round(((i + 1) / images.length) * 100));
    }

    setImages(updatedImages);
    setIsProcessing(false);
    toast.success(`Resized for ${preset.name}!`);
  };

  const downloadSingle = (image) => {
    if (!image.resized) return;
    const presetName = `instagram_${image.presetUsed}`;
    const newName = image.name.replace(/\.[^/.]+$/, `_${presetName}.jpg`);
    saveAs(image.resized, newName);
  };

  const downloadAll = async () => {
    const resizedImages = images.filter(img => img.resized);
    if (resizedImages.length === 0) {
      toast.error("No resized images to download");
      return;
    }

    if (resizedImages.length === 1) {
      downloadSingle(resizedImages[0]);
      return;
    }

    const zip = new JSZip();
    resizedImages.forEach((image) => {
      const presetName = `instagram_${image.presetUsed}`;
      const newName = image.name.replace(/\.[^/.]+$/, `_${presetName}.jpg`);
      zip.file(newName, image.resized);
    });

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, `instagram_${selectedPreset}_images.zip`);
  };

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const clearAll = () => {
    setImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddMore = (e) => {
    const files = e.target.files;
    if (files) {
      handleFileSelect({ target: { files } });
    }
    e.target.value = '';
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-pink-50 to-purple-50'}`}>
      <Toaster position="top-center" richColors />
      
      {!embedded && (
        <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                <Instagram className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
            </a>
            <div className="flex items-center gap-4">
              <ToolsNav />
              <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
                <Info className="w-3 h-3" /> 100% Local Processing
              </Badge>
              <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </header>
      )}

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full mb-4">
            <Instagram className="w-5 h-5" />
            <span className="font-medium">Instagram</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Resize Image for Instagram
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Perfect dimensions for Instagram posts, stories, reels and profile pictures.
          </p>
        </div>

        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <label className="text-base font-semibold text-slate-800 dark:text-white mb-4 block">
              Select Format
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {Object.entries(PRESETS).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => setSelectedPreset(key)}
                  data-testid={`preset-${key}`}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    selectedPreset === key 
                      ? 'border-transparent bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-lg scale-105' 
                      : 'border-slate-200 dark:border-slate-700 hover:border-pink-300 dark:hover:border-pink-600 bg-white dark:bg-slate-800'
                  }`}
                >
                  <Instagram className={`w-6 h-6 mx-auto mb-2 ${selectedPreset === key ? 'text-white' : 'text-pink-500'}`} />
                  <p className={`text-sm font-medium ${selectedPreset === key ? 'text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                    {value.name.replace('Instagram ', '')}
                  </p>
                  <p className={`text-xs mt-1 ${selectedPreset === key ? 'text-white/80' : 'text-slate-500'}`}>
                    {value.width} × {value.height}
                  </p>
                </button>
              ))}
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-4 text-center">
              {preset.description}
            </p>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
            isDragging ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div 
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-pink-500" />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                Drop, paste, or click to upload
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Supports JPEG, PNG, WebP, GIF, HEIC
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 text-white text-sm">
                <Instagram className="w-4 h-4" />
                Resizing to {preset.width} × {preset.height}
              </div>
            </div>
          </CardContent>
        </Card>

        {isProcessing && (
          <Card className="mt-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Progress value={resizeProgress} className="h-3" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                  {resizeProgress}%
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {images.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Images ({images.length})
              </h3>
              <div className="flex gap-2 flex-wrap">
                <input
                  ref={addMoreInputRef}
                  type="file"
                  accept="image/*,.heic"
                  multiple
                  onChange={handleAddMore}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => addMoreInputRef.current?.click()}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                <Button 
                  onClick={handleResize} 
                  disabled={isProcessing}
                  data-testid="resize-button"
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90"
                >
                  <Instagram className="w-4 h-4 mr-2" />
                  Resize for {preset.name.replace('Instagram ', '')}
                </Button>
              </div>
            </div>

            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image) => (
                <Card key={image.id} className="dark:bg-slate-800 overflow-hidden" data-testid={`image-card-${image.id}`}>
                  <div className="aspect-video relative bg-slate-100 dark:bg-slate-700">
                    <img
                      src={image.resizedPreview || image.preview}
                      alt={image.name}
                      className="w-full h-full object-contain"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 w-8 h-8"
                      onClick={() => removeImage(image.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                    {image.resized && (
                      <Badge className="absolute top-2 left-2 bg-gradient-to-r from-pink-500 to-purple-600">
                        {PRESETS[image.presetUsed]?.name || 'Resized'}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <p className="font-medium text-slate-800 dark:text-white truncate mb-2">{image.name}</p>
                    <div className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                      <p>Original: {image.originalWidth} × {image.originalHeight}</p>
                      {image.resized && (
                        <p className="text-green-600 dark:text-green-400">
                          Resized: {image.resizedWidth} × {image.resizedHeight} ({formatSize(image.resizedSize)})
                        </p>
                      )}
                    </div>
                    {image.resized && (
                      <Button
                        size="sm"
                        className="w-full mt-3 bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90"
                        onClick={() => downloadSingle(image)}
                        data-testid={`download-${image.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" /> Download
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {images.some(img => img.resized) && images.length > 1 && (
              <div className="mt-4 text-center">
                <Button onClick={downloadAll} size="lg" className="bg-gradient-to-r from-pink-500 to-purple-600 hover:opacity-90" data-testid="download-all">
                  <Download className="w-5 h-5 mr-2" /> Download All (ZIP)
                </Button>
              </div>
            )}
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: Instagram, title: "Instagram Ready", desc: "Perfect dimensions for all Instagram formats" },
            { icon: Zap, title: "Smart Cropping", desc: "Auto-crops to fit while keeping the subject centered" },
            { icon: Info, title: "100% Private", desc: "All processing happens in your browser" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <feature.icon className="w-10 h-10 mx-auto mb-3 text-pink-500" />
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      {pageContent?.content && (
        <div className="bg-slate-50 dark:bg-slate-800/50 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}

      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ResizeForInstagram;
